export const workingFinanceCourse = {
  id: "finance-mastery-complete-2024",
  title: "Finance Mastery: Complete Investment & Analysis Course",
  shortDescription: "Complete 24+ hour comprehensive finance course covering everything from fundamentals to advanced portfolio management, derivatives, and corporate finance.",
  description: "Master finance from fundamentals to advanced corporate finance, derivatives, and portfolio management with 24+ hours of comprehensive learning including real-time market analysis, financial modeling, risk management, and practical case studies from real companies.",
  category: "Finance",
  industryCategory: "Financial Services",
  difficulty: "intermediate",
  duration: 24,
  estimatedWeeks: 12,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from certified financial professionals and market analysts",
  thumbnailUrl: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),
  
  learningObjectives: [
    "Analyze financial statements and key performance metrics",
    "Build dynamic financial models and projections",
    "Evaluate stocks using real-time market data",
    "Create balanced investment portfolios",
    "Calculate risk-adjusted returns and portfolio optimization",
    "Master DCF valuation and comparative analysis",
    "Apply modern portfolio theory principles",
    "Develop trading and investment strategies"
  ],

  prerequisites: [
    "Basic Mathematics",
    "Excel/Google Sheets",
    "Economic Fundamentals"
  ],

  skills: [
    "Financial Analysis",
    "Stock Valuation",
    "Portfolio Management", 
    "Risk Assessment",
    "Financial Modeling",
    "Market Analysis",
    "Investment Strategy",
    "Balance Sheet Analysis"
  ],

  tags: [
    "Finance",
    "Investing", 
    "Stocks",
    "Portfolio",
    "Analysis",
    "Markets",
    "Valuation",
    "Risk Management"
  ],

  marketData: {
    averageSalary: 95000,
    salaryRange: {
      min: 65000,
      max: 145000
    },
    jobDemand: 4,
    marketTrend: "growing",
    careerPaths: [
      "Financial Analyst",
      "Portfolio Manager", 
      "Investment Advisor",
      "Risk Manager"
    ],
    relatedSkills: [
      "Excel",
      "Python",
      "Bloomberg Terminal",
      "Financial Modeling",
      "CFA",
      "Statistics"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "finance-fundamentals",
      title: "Financial Fundamentals",
      description: "Master core financial concepts, statements, and analysis techniques",
      order: 1,
      estimatedHours: 3.5,
      content: [
        {
          id: "intro-to-finance",
          title: "Introduction to Finance",
          description: "Understanding financial markets, instruments, and key concepts",
          type: "video",
          duration: 20,
        },
        {
          id: "financial-statements",
          title: "Reading Financial Statements",
          description: "Master income statements, balance sheets, and cash flow statements",
          type: "text",
          duration: 25,
          textContent: `# Financial Statements Mastery

## The Three Core Financial Statements

Financial statements are the backbone of financial analysis. They provide a comprehensive view of a company's financial health and performance. Understanding these statements is crucial for investors, analysts, creditors, and management.

### 1. Income Statement (Profit & Loss Statement)

The Income Statement shows profitability over a specific period (quarter or year):

**Revenue Components:**
- **Gross Revenue**: Total sales before any deductions
- **Net Revenue**: Revenue after returns, allowances, and discounts
- **Operating Revenue**: Revenue from core business operations
- **Non-Operating Revenue**: Interest income, investment gains, etc.

**Cost Structure:**
- **Cost of Goods Sold (COGS)**: Direct costs of producing goods/services
  - Materials, labor, manufacturing overhead
  - For service companies: direct service delivery costs
- **Gross Profit**: Revenue - COGS (measures production efficiency)

**Operating Expenses:**
- **Selling Expenses**: Marketing, sales commissions, advertising
- **General & Administrative (G&A)**: Management salaries, office rent, utilities
- **Research & Development (R&D)**: Innovation and product development costs
- **Depreciation & Amortization**: Allocation of asset costs over time

**Profitability Metrics:**
- **EBITDA**: Earnings Before Interest, Taxes, Depreciation, and Amortization
- **EBIT**: Earnings Before Interest and Taxes (Operating Income)
- **EBT**: Earnings Before Taxes
- **Net Income**: Final profit after all expenses and taxes

### 2. Balance Sheet - The Financial Snapshot

Shows financial position at a specific point in time. Must always balance: **Assets = Liabilities + Shareholders' Equity**

**ASSETS (What the company owns):**

*Current Assets (liquid within 1 year):*
- **Cash and Cash Equivalents**: Most liquid assets
- **Short-term Investments**: Marketable securities
- **Accounts Receivable**: Money owed by customers
- **Inventory**: Raw materials, work-in-progress, finished goods
- **Prepaid Expenses**: Insurance, rent paid in advance
- **Other Current Assets**: Tax refunds, short-term loans to others

*Non-Current Assets (long-term):*
- **Property, Plant & Equipment (PPE)**: Buildings, machinery, equipment
- **Accumulated Depreciation**: Cumulative wear and tear on assets
- **Intangible Assets**: Patents, trademarks, goodwill, software
- **Long-term Investments**: Stocks, bonds held for >1 year
- **Deferred Tax Assets**: Future tax benefits

**LIABILITIES (What the company owes):**

*Current Liabilities (due within 1 year):*
- **Accounts Payable**: Money owed to suppliers
- **Short-term Debt**: Bank loans, commercial paper due <1 year
- **Accrued Expenses**: Wages, interest, taxes owed but not yet paid
- **Deferred Revenue**: Payment received for services not yet provided
- **Current Portion of Long-term Debt**: Principal payments due this year

*Long-term Liabilities:*
- **Long-term Debt**: Bonds, mortgages, bank loans due >1 year
- **Deferred Tax Liabilities**: Future tax obligations
- **Pension Obligations**: Retirement benefits owed to employees
- **Lease Obligations**: Future lease payments

**SHAREHOLDERS' EQUITY (Owner's stake):**
- **Common Stock**: Par value of outstanding shares
- **Additional Paid-in Capital**: Premium paid above par value
- **Retained Earnings**: Accumulated profits not distributed as dividends
- **Accumulated Other Comprehensive Income**: Currency translations, unrealized gains/losses
- **Treasury Stock**: Company's own shares repurchased (reduces equity)

### 3. Cash Flow Statement - The Reality Check

Tracks actual cash movements (not accounting profits). Critical because companies can be profitable but still run out of cash.

**Operating Cash Flow (OCF):**
- Starts with Net Income
- Adjusts for non-cash items (depreciation, amortization)
- Adjusts for working capital changes
- Shows cash from core business operations

**Investing Cash Flow:**
- **Capital Expenditures (CapEx)**: Purchases of PPE
- **Acquisitions**: Buying other companies
- **Asset Sales**: Selling equipment, subsidiaries
- **Investment Purchases/Sales**: Buying/selling securities

**Financing Cash Flow:**
- **Debt Issuance/Repayment**: Borrowing or paying back loans
- **Equity Issuance/Repurchase**: Issuing new shares or buybacks
- **Dividend Payments**: Cash returned to shareholders
- **Interest Payments**: Cost of debt financing

**Free Cash Flow = Operating Cash Flow - Capital Expenditures**
This is the cash available for growth, debt repayment, dividends, or acquisitions.

## Advanced Financial Ratios

### Profitability Analysis

**Margin Analysis:**
- **Gross Margin** = Gross Profit / Revenue
  - Measures production efficiency and pricing power
  - Industry benchmarks: Software (80-90%), Retail (20-30%)
- **Operating Margin** = Operating Income / Revenue
  - Shows operational efficiency after all operating expenses
- **Net Margin** = Net Income / Revenue
  - Bottom-line profitability including all expenses
- **EBITDA Margin** = EBITDA / Revenue
  - Cash generation before financing and accounting decisions

**Return Metrics:**
- **ROE (Return on Equity)** = Net Income / Shareholders' Equity
  - Measures returns generated on shareholders' investments
  - DuPont Formula: ROE = (Net Margin) × (Asset Turnover) × (Equity Multiplier)
- **ROA (Return on Assets)** = Net Income / Total Assets
  - Measures how efficiently assets generate profits
- **ROIC (Return on Invested Capital)** = NOPAT / Invested Capital
  - Measures returns on all invested capital (debt + equity)
- **ROI (Return on Investment)** = (Gain - Cost) / Cost
  - Measures efficiency of specific investments

### Liquidity Analysis (Short-term Financial Health)

- **Current Ratio** = Current Assets / Current Liabilities
  - General benchmark: 1.5-3.0 (varies by industry)
  - Measures ability to pay short-term obligations
- **Quick Ratio (Acid Test)** = (Current Assets - Inventory) / Current Liabilities
  - More conservative liquidity measure
  - Benchmark: >1.0 preferred
- **Cash Ratio** = Cash / Current Liabilities
  - Most conservative liquidity measure
- **Working Capital** = Current Assets - Current Liabilities
  - Absolute measure of short-term liquidity
- **Operating Cash Flow Ratio** = Operating Cash Flow / Current Liabilities
  - Measures cash-based ability to pay short-term debts

### Leverage Analysis (Capital Structure)

- **Debt-to-Equity Ratio** = Total Debt / Total Equity
  - Measures financial leverage
  - Benchmark varies by industry (utilities high, tech low)
- **Debt-to-Assets Ratio** = Total Debt / Total Assets
  - Shows what percentage of assets are financed by debt
- **Interest Coverage Ratio** = EBIT / Interest Expense
  - Measures ability to pay interest on debt
  - Benchmark: >2.5 generally safe
- **Debt Service Coverage Ratio** = Operating Income / Total Debt Service
  - Measures ability to service total debt obligations
- **Equity Multiplier** = Total Assets / Shareholders' Equity
  - Shows financial leverage (higher = more leveraged)
- **Capitalization Ratio** = Long-term Debt / (Long-term Debt + Shareholders' Equity)
  - Measures long-term capital structure

### Efficiency Analysis (Asset Management)

**Turnover Ratios:**
- **Asset Turnover** = Revenue / Average Total Assets
  - Measures how efficiently assets generate revenue
- **Inventory Turnover** = COGS / Average Inventory
  - Days Sales in Inventory = 365 / Inventory Turnover
  - Higher turnover generally better (less capital tied up)
- **Receivables Turnover** = Revenue / Average Accounts Receivable
  - Days Sales Outstanding = 365 / Receivables Turnover
  - Measures collection efficiency
- **Payables Turnover** = COGS / Average Accounts Payable
  - Days Payable Outstanding = 365 / Payables Turnover
  - Measures payment timing to suppliers

**Cash Conversion Cycle:**
DSO + DIO - DPO = Cash Conversion Cycle
- Measures how long cash is tied up in operations
- Shorter cycle = better cash flow management

### Advanced Valuation Ratios

**Market-Based Ratios (for public companies):**
- **Price-to-Earnings (P/E)** = Stock Price / Earnings Per Share
  - Forward P/E uses projected earnings
  - Trailing P/E uses historical earnings
- **Price-to-Book (P/B)** = Stock Price / Book Value Per Share
  - Compares market value to accounting value
- **Price-to-Sales (P/S)** = Market Cap / Revenue
  - Useful for companies without profits
- **Enterprise Value (EV)** = Market Cap + Total Debt - Cash
  - **EV/Revenue**: Enterprise value relative to sales
  - **EV/EBITDA**: Enterprise value relative to cash earnings

Understanding these fundamentals is essential for all investment and financial decisions.`
        },
        {
          id: "balance-sheet-interactive",
          title: "Balance Sheet Balancing Exercise",
          description: "Interactive exercise to balance a company's balance sheet",
          type: "finance-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "balance-sheet",
            instructions: "Complete the balance sheet by ensuring Assets = Liabilities + Equity. Calculate the missing Retained Earnings.",
            scenario: {
              assets: {
                cash: 50000,
                accountsReceivable: 25000,
                inventory: 30000,
                equipment: 100000
              },
              liabilities: {
                accountsPayable: 15000,
                notesPayable: 40000,
                longTermDebt: 75000
              },
              equity: {
                commonStock: 40000
              }
            },
            hints: [
              "Remember: Assets = Liabilities + Equity",
              "Total all assets first, then all liabilities",
              "Retained Earnings = Total Assets - Total Liabilities - Common Stock"
            ]
          }
        }
      ],
      quiz: {
        id: "fundamentals-quiz",
        title: "Financial Fundamentals Quiz",
        description: "Test your understanding of financial statements and ratios",
        timeLimit: 15,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which statement shows a company's profitability over a period of time?",
            options: [
              "Balance Sheet",
              "Income Statement", 
              "Cash Flow Statement",
              "Statement of Equity"
            ],
            correctAnswer: 1,
            explanation: "The Income Statement (P&L) shows revenues, expenses, and profits over a specific time period."
          },
          {
            id: "q2", 
            type: "multiple-choice",
            question: "What does the current ratio measure?",
            options: [
              "Profitability",
              "Liquidity",
              "Leverage", 
              "Efficiency"
            ],
            correctAnswer: 1,
            explanation: "The current ratio (Current Assets / Current Liabilities) measures a company's ability to pay short-term debts."
          },
          {
            id: "q3",
            type: "multiple-choice", 
            question: "In the accounting equation, Assets equals:",
            options: [
              "Liabilities + Equity",
              "Revenue - Expenses",
              "Cash + Investments",
              "Debt + Capital"
            ],
            correctAnswer: 0,
            explanation: "The fundamental accounting equation is Assets = Liabilities + Shareholders' Equity."
          }
        ]
      }
    },

    {
      id: "stock-analysis",
      title: "Stock Analysis & Valuation", 
      description: "Learn to analyze and value stocks using DCF models and ratio analysis",
      order: 2,
      estimatedHours: 4.0,
      content: [
        {
          id: "stock-basics",
          title: "Stock Market Fundamentals",
          description: "Understanding stocks, markets, and trading basics",
          type: "video",
          duration: 25
        },
        {
          id: "valuation-methods",
          title: "Stock Valuation Methods",
          description: "DCF, P/E, PEG, and other valuation techniques",
          type: "video", 
          duration: 35
        },
        {
          id: "dcf-interactive",
          title: "DCF Valuation Model",
          description: "Build a complete DCF model for valuing companies",
          type: "finance-interactive",
          duration: 45,
          interactiveData: {
            exerciseType: "dcf-model",
            instructions: "Build a comprehensive DCF model by projecting free cash flows and calculating intrinsic value per share.",
            scenario: {
              companyData: {
                currentFCF: 1000,
                growthRates: [0.20, 0.18, 0.15, 0.12, 0.10],
                wacc: 0.10,
                terminalGrowthRate: 0.03,
                sharesOutstanding: 100
              }
            },
            hints: [
              "Project FCF for each year using growth rates",
              "Calculate present value of each FCF using WACC",
              "Don't forget to include terminal value",
              "Divide enterprise value by shares outstanding"
            ]
          }
        },
        {
          id: "ratio-analysis-interactive",
          title: "Financial Ratio Analysis",
          description: "Calculate and interpret key financial ratios",
          type: "finance-interactive",
          duration: 35,
          interactiveData: {
            exerciseType: "ratio-analysis",
            instructions: "Calculate key financial ratios using the provided financial data.",
            scenario: {
              financialData: {
                currentAssets: 150000,
                inventory: 30000,
                currentLiabilities: 75000,
                totalDebt: 200000,
                totalEquity: 300000,
                netIncome: 45000,
                totalAssets: 500000
              }
            },
            hints: [
              "Current Ratio = Current Assets / Current Liabilities",
              "Quick Ratio excludes inventory from current assets",
              "ROE = Net Income / Total Equity × 100",
              "All percentages should be calculated properly"
            ]
          }
        }
      ],
      quiz: {
        id: "stock-analysis-quiz",
        title: "Stock Analysis Quiz",
        description: "Test your stock valuation knowledge",
        timeLimit: 12,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What does a P/E ratio of 25 mean?",
            options: [
              "The stock will return 25% annually",
              "Investors pay $25 for every $1 of earnings",
              "The stock price is $25",
              "The company has 25% profit margin"
            ],
            correctAnswer: 1,
            explanation: "P/E ratio shows how much investors are willing to pay per dollar of annual earnings."
          },
          {
            id: "q2",
            type: "multiple-choice", 
            question: "In a DCF model, what does WACC represent?",
            options: [
              "Weighted Average Cost of Capital - the discount rate",
              "Working Asset Capital Calculation",
              "Weighted Asset Cost Calculation", 
              "Working Average Cash Conversion"
            ],
            correctAnswer: 0,
            explanation: "WACC is the weighted average cost of capital, used as the discount rate in DCF models."
          }
        ]
      }
    },

    {
      id: "portfolio-management",
      title: "Portfolio Management",
      description: "Build and optimize investment portfolios using modern portfolio theory",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "portfolio-theory",
          title: "Modern Portfolio Theory",
          description: "Understanding diversification, risk, and return optimization",
          type: "video",
          duration: 30
        },
        {
          id: "asset-allocation",
          title: "Asset Allocation Strategies", 
          description: "Strategic and tactical asset allocation approaches",
          type: "text",
          duration: 25,
          textContent: `# Asset Allocation Strategies

## Strategic Asset Allocation

Strategic asset allocation is the long-term approach to portfolio construction based on:
- **Investment Goals**: Growth, income, or preservation
- **Time Horizon**: Years until you need the money
- **Risk Tolerance**: Ability and willingness to accept volatility

### Common Allocation Models

**Conservative (Low Risk)**
- Stocks: 20-40%
- Bonds: 50-70% 
- Alternatives: 5-10%
- Cash: 5-15%

**Moderate (Balanced)**
- Stocks: 50-70%
- Bonds: 25-40%
- Alternatives: 5-15%
- Cash: 0-10%

**Aggressive (High Growth)**
- Stocks: 80-95%
- Bonds: 5-15%
- Alternatives: 5-10%
- Cash: 0-5%

## Tactical Asset Allocation

Tactical allocation involves short-term adjustments based on:
- Market conditions
- Economic cycles
- Valuation levels
- Technical indicators

Effective asset allocation is the most important driver of long-term investment success.`
        },
        {
          id: "portfolio-optimizer",
          title: "Portfolio Optimization Tool",
          description: "Build an optimal portfolio using risk-return analysis",
          type: "finance-interactive",
          duration: 40,
          interactiveData: {
            exerciseType: "portfolio-optimization",
            instructions: "Build an optimal portfolio by selecting asset weights to maximize risk-adjusted returns (Sharpe ratio).",
            scenario: {
              assets: [
                { symbol: "SPY", expectedReturn: 0.10, volatility: 0.16, name: "S&P 500 ETF" },
                { symbol: "BND", expectedReturn: 0.04, volatility: 0.05, name: "Total Bond Market ETF" },
                { symbol: "VEA", expectedReturn: 0.08, volatility: 0.18, name: "International Developed ETF" },
                { symbol: "VNQ", expectedReturn: 0.07, volatility: 0.20, name: "Real Estate ETF" }
              ]
            },
            hints: [
              "Weights must sum to 100%",
              "Consider correlation between assets for diversification",
              "Higher Sharpe ratio indicates better risk-adjusted returns",
              "Target Sharpe ratio above 0.6 is excellent"
            ]
          }
        }
      ],
      quiz: {
        id: "portfolio-quiz",
        title: "Portfolio Management Quiz", 
        description: "Test your portfolio construction knowledge",
        timeLimit: 10,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the primary benefit of diversification?",
            options: [
              "Increases expected returns",
              "Reduces portfolio risk without sacrificing expected returns",
              "Guarantees positive returns", 
              "Eliminates all investment risk"
            ],
            correctAnswer: 1,
            explanation: "Diversification reduces risk through combining uncorrelated assets without necessarily reducing expected returns."
          }
        ]
      }
    },

    {
      id: "derivatives-risk-management",
      title: "Derivatives & Risk Management",
      description: "Master options pricing and advanced risk management techniques",
      order: 4,
      estimatedHours: 4.0,
      content: [
        {
          id: "options-fundamentals",
          title: "Options Trading Fundamentals",
          description: "Understanding calls, puts, and basic option strategies",
          type: "video",
          duration: 35
        },
        {
          id: "options-strategies",
          title: "Advanced Options Strategies",
          description: "Learn spreads, straddles, and complex option positions",
          type: "text",
          duration: 40,
          textContent: `# Advanced Options Strategies

## Single-Leg Strategies

### Long Call
- **Setup**: Buy a call option
- **Outlook**: Bullish on underlying stock
- **Max Profit**: Unlimited above strike + premium
- **Max Loss**: Premium paid
- **Breakeven**: Strike price + premium paid

### Long Put
- **Setup**: Buy a put option
- **Outlook**: Bearish on underlying stock
- **Max Profit**: Strike price - premium paid
- **Max Loss**: Premium paid
- **Breakeven**: Strike price - premium paid

### Covered Call
- **Setup**: Own 100 shares + sell 1 call
- **Outlook**: Neutral to slightly bullish
- **Max Profit**: Strike price - stock cost + premium received
- **Max Loss**: Stock cost - premium received
- **Use Case**: Generate income on existing holdings

## Greeks Management
- **Delta**: Directional exposure (0-1 for calls, 0 to -1 for puts)
- **Gamma**: Rate of delta change (higher for ATM options)
- **Theta**: Time decay (works against long options)
- **Vega**: Volatility sensitivity (higher for longer-dated options)

Understanding derivatives and risk management is crucial for sophisticated portfolio management.`
        },
        {
          id: "options-pricing-interactive",
          title: "Options Pricing Calculator",
          description: "Calculate option values using Black-Scholes model",
          type: "finance-interactive",
          duration: 40,
          interactiveData: {
            exerciseType: "options-pricing",
            instructions: "Calculate option prices and Greeks using the Black-Scholes model with the given parameters.",
            scenario: {
              optionParams: {
                stockPrice: 100,
                strike: 100,
                timeToExpiration: 30/365,
                volatility: 0.25,
                riskFreeRate: 0.05,
                dividendYield: 0.02
              }
            },
            hints: [
              "Use Black-Scholes formula for European options",
              "Delta measures price sensitivity to stock moves",
              "Theta shows daily time decay",
              "At-the-money options have highest gamma"
            ]
          }
        }
      ],
      quiz: {
        id: "derivatives-quiz",
        title: "Derivatives & Risk Management Quiz",
        description: "Test your knowledge of options and risk management",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the maximum loss for a long call option?",
            options: [
              "Unlimited",
              "The premium paid",
              "The strike price",
              "Zero"
            ],
            correctAnswer: 1,
            explanation: "The maximum loss for a long call is limited to the premium paid for the option."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which Greek measures an option's sensitivity to time decay?",
            options: [
              "Delta",
              "Gamma",
              "Theta",
              "Vega"
            ],
            correctAnswer: 2,
            explanation: "Theta measures time decay - how much an option's value decreases as time passes."
          }
        ]
      }
    },

    {
      id: "fixed-income-securities",
      title: "Fixed Income Securities",
      description: "Bond analysis, yield curves, and fixed income portfolio management",
      order: 5,
      estimatedHours: 3.5,
      content: [
        {
          id: "bond-fundamentals",
          title: "Bond Valuation and Analysis",
          description: "Understanding bond pricing, duration, and convexity",
          type: "text",
          duration: 35,
          textContent: `# Fixed Income Securities Analysis

## Bond Fundamentals

### Basic Bond Components
- **Face Value (Par)**: Principal amount paid at maturity ($1,000 typical)
- **Coupon Rate**: Annual interest rate (e.g., 5% = $50/year on $1,000 bond)
- **Maturity**: Time until principal repayment
- **Credit Quality**: Issuer's ability to make payments (AAA to D rating)

### Bond Pricing Formula
**Present Value of Cash Flows:**
P = Σ[C/(1+r)^t] + FV/(1+r)^n

Where:
- P = Bond price
- C = Coupon payment
- r = Required yield (discount rate)
- t = Time period
- FV = Face value
- n = Number of periods to maturity

### Duration and Convexity

**Modified Duration**
- Price sensitivity to yield changes
- Rule: 1% yield change = Modified Duration % price change (opposite direction)

**Convexity**
- Measures curvature of price-yield relationship
- Duration assumes linear relationship (approximation)
- Convexity corrects for this assumption

Fixed income securities provide steady income and diversification benefits, but require careful analysis of credit, interest rate, and inflation risks.`
        },
        {
          id: "bond-valuation-interactive",
          title: "Bond Valuation Calculator",
          description: "Calculate bond prices and risk metrics",
          type: "finance-interactive",
          duration: 35,
          interactiveData: {
            exerciseType: "bond-valuation",
            instructions: "Calculate bond price and risk metrics using present value techniques and duration analysis.",
            scenario: {
              bondParams: {
                faceValue: 1000,
                couponRate: 0.06,
                maturity: 5,
                marketYield: 0.05
              }
            },
            hints: [
              "Bond price = PV of coupons + PV of principal",
              "When yield < coupon rate, bond trades at premium",
              "Duration measures price sensitivity to yield changes",
              "Higher coupon bonds have lower duration"
            ]
          }
        }
      ]
    },

    {
      id: "corporate-finance",
      title: "Corporate Finance",
      description: "Capital structure, M&A, and corporate financial decision making",
      order: 6,
      estimatedHours: 3.5,
      content: [
        {
          id: "capital-structure",
          title: "Capital Structure Optimization",
          description: "Understanding debt vs equity financing and optimal capital structure",
          type: "text",
          duration: 45,
          textContent: `# Capital Structure Optimization

## Fundamentals of Capital Structure

Capital structure refers to the mix of debt and equity financing a company uses to fund its operations and growth. The optimal capital structure maximizes firm value while minimizing the cost of capital.

### Key Components

**Equity Financing:**
- **Common Stock**: Voting rights, residual claims, unlimited upside
- **Preferred Stock**: Fixed dividends, priority over common stock
- **Retained Earnings**: Internal funding from accumulated profits

**Debt Financing:**
- **Bank Loans**: Negotiated terms, relationship-based
- **Corporate Bonds**: Market-traded, standardized terms
- **Commercial Paper**: Short-term, high-grade companies only

## Cost of Capital Calculations

### Weighted Average Cost of Capital (WACC)
**Formula**: WACC = (E/V × R_E) + (D/V × R_D × (1-T_C))

Where:
- E = Market value of equity
- D = Market value of debt  
- V = E + D (total firm value)
- R_E = Cost of equity
- R_D = Cost of debt
- T_C = Corporate tax rate

Understanding capital structure optimization helps companies minimize their cost of capital and maximize firm value while maintaining adequate financial flexibility.`
        },
        {
          id: "ma-analysis",
          title: "Mergers & Acquisitions Analysis",
          description: "Valuation methods, deal structures, and M&A financial modeling",
          type: "video",
          duration: 50
        }
      ]
    }
  ],

  assignments: [
    {
      id: "financial-analysis-project",
      title: "Company Financial Analysis Project",
      description: "Complete financial analysis of a public company including ratio analysis, valuation, and investment recommendation",
      type: "project",
      dueWeeks: 4,
      maxScore: 100,
      instructions: `# Company Financial Analysis Project

## Objective
Conduct a comprehensive financial analysis of a publicly-traded company and provide an investment recommendation.

## Requirements
1. **Company Selection**: Choose a large-cap company from S&P 500
2. **Financial Statement Analysis**: Analyze 5 years of financial statements
3. **Ratio Analysis**: Calculate and interpret key financial ratios
4. **Peer Comparison**: Compare to 2-3 industry competitors
5. **Valuation**: Use DCF and relative valuation methods
6. **Investment Recommendation**: Buy, Hold, or Sell with price target

## Deliverables
- 15-20 page analysis report
- Excel financial model with calculations
- PowerPoint presentation (10-15 slides)`,
      submissionFormat: "pdf",
      resources: [
        "SEC EDGAR database for 10-K filings",
        "Yahoo Finance for market data",
        "Industry reports from course library"
      ]
    }
  ],

  finalExam: {
    id: "finance-mastery-final",
    title: "Finance Mastery Final Exam",
    description: "Comprehensive exam covering all finance concepts and analysis techniques",
    timeLimit: 90,
    passingScore: 85,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "In a DCF model, what does WACC represent?",
        options: [
          "Weighted Average Cost of Capital - the required rate of return",
          "Working Asset Capital Calculation",
          "Weighted Asset Cost Calculation", 
          "Working Average Cash Conversion"
        ],
        correctAnswer: 0,
        explanation: "WACC is the weighted average cost of capital, representing the required rate of return for the company's financing."
      },
      {
        id: "final-q2", 
        type: "multiple-choice",
        question: "Which ratio best measures a company's ability to pay short-term obligations?",
        options: [
          "Debt-to-equity ratio",
          "Current ratio",
          "Return on equity",
          "Price-to-earnings ratio"
        ],
        correctAnswer: 1,
        explanation: "The current ratio (current assets / current liabilities) measures liquidity and ability to pay short-term debts."
      }
    ]
  },

  quizzes: []
};